import Box from '@mui/material/Box';
import { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useParams } from 'react-router-dom';
import Papa from "papaparse";
import PageTitle from '../../../../components/PageTitle';
import { ACCESS_TOKEN_KEY, currentOrgId , currentUser } from '../../../../store';
import Loader from '../../../../components/Loader';
import FlexArea from '../../../../components/Flex/FlexArea';

const parseCSV = (csvString) => {
  return new Promise((resolve, reject) => {
    Papa.parse(csvString, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: (result) => resolve(result.data),
      error: (error) => reject(error),
    });
  });
};

const FlexReport = () => {
  const { moduleId } = useParams();

  const user = useRecoilValue(currentUser);
  const orgID = useRecoilValue(currentOrgId);

  const [isLoading, setLoading] = useState(true);
  const [isStreaming, setStreaming] = useState(true);
  const [, setWindowHeight] = useState('560px');
  const lastUpdatedPercent = useRef(0);
  const [startTime, setStartTime] = useState(Date.now());
  const [currentSize, setCurrentSize] = useState(0);
  const [estimatedSize, setEstimatedSize] = useState(null);
  const [flexData, updateFlexData] = useState([]);
  const [yamlData, updateYamlData] = useState([]);

  const loadFlexData = async (signal) => {
    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}/v1/api/bigquery/${orgID}/${moduleId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY)}`,
        },
        signal,
      });

      if (!res.body) throw new Error("No response body");

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let streamedYamlData = null;
      let streamedEstimatedSize = null;
      let streamedCsvString = "";
      let currentTotal = 0;
      let leftover = "";
      let lineUpdateCounter = 0;

      const processStream = async () => {
        if (signal.aborted) {
          reader.cancel();
          console.log("Stream reader also aborted.");
          return;
        }

        const { value, done } = await reader.read();
        if (done) {
          if (leftover) {
            streamedCsvString += leftover;
            currentTotal += leftover.length;
          }
          setCurrentSize(currentTotal);
          return;
        }

        const text = decoder.decode(value, { stream: true });
        const combined = leftover + text;
        const lines = combined.split("\n");
        leftover = lines.pop();

        lines.forEach(line => {
          if (streamedYamlData === null) {
            const { yaml } = JSON.parse(line);
            streamedYamlData = yaml;
            updateYamlData(yaml);
            return
          }

          if (streamedEstimatedSize === null) {
            const est = parseInt(line.trim(), 10);
            streamedEstimatedSize = est;
            setEstimatedSize(est);
            setLoading(false);
            setStreaming(true);
            setStartTime(Date.now());
            return;
          }

          streamedCsvString += `${line}\n`;
          currentTotal += line.length + 1;
          lineUpdateCounter++;

          const percentDone = Math.floor(100 * currentTotal / streamedEstimatedSize);
          if (percentDone !== lastUpdatedPercent.current && lineUpdateCounter % 2500 === 0) {
            setCurrentSize(currentTotal);
            lastUpdatedPercent.current = percentDone;
          }
        });
        return processStream();
      };

      await processStream();

      setCurrentSize(streamedEstimatedSize);
      lastUpdatedPercent.current = 100;

      // NOTE: gives the UI time to settle before processing All That Data
      await new Promise((resolve) => setTimeout(resolve, 50));

      const data = await parseCSV(streamedCsvString);
      updateFlexData(data);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Flex stream aborted.');
      } else {
        console.log(error);
      }
    } finally {
      setCurrentSize(estimatedSize);
      setStreaming(false);
    }
  };

  useEffect(() => {
    if (!orgID) return;
    if (window.innerHeight > 750) {
      setWindowHeight('620px')
    }
    const abortController = new AbortController();
    loadFlexData(abortController.signal).then();
    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgID]);

  if (isLoading) return <Loader />;

  const report = user
    ?.userPermissions
    ?.filter(m => m.id === parseInt(moduleId, 10));

  const pageTitle = report?.length > 0 ? report[0].name : 'Flex reporting';

  return (
    <Box sx={{
      padding: '25px',
      display: 'flex',
      flexDirection: 'column',
      rowGap: '6px',
      height: '100%'
    }}
    >
      <PageTitle>
        {pageTitle}
      </PageTitle>
      <FlexArea
        id="flex_report"
        currentSize={currentSize}
        data={flexData}
        estimatedSize={estimatedSize}
        startTime={startTime}
        streaming={isStreaming}
        yaml={yamlData}
        style={{
          width: '100%',
          height: '100%'
        }}
      />
    </Box>
  )
}

export default FlexReport;
